/** Marks a `rejectRequest('fields', …)` payload, whose other keys hold a message map per field. */
export const FIELDS_ERROR = 'fields'

interface RejectedCall {
    status?: unknown
    statusText?: unknown
    error?: unknown
    [field: string]: unknown
}

/**
 * Narrows a rejected `call` to the per-field message maps it carries; no other rejection shape has
 * them. Only the marker is checked, so `T` states which fields the calling endpoint validates.
 */
export const isFieldsRejection = <T extends object>(rejection: unknown): rejection is T =>
    typeof rejection === 'object' &&
    rejection !== null &&
    (rejection as { error?: unknown }).error === FIELDS_ERROR

/**
 * Every message inside a field-validation payload, in which each field maps a failed rule to its
 * message: `{ nr_leads: { _empty: 'This field cannot be left empty' } }`.
 */
const getFieldMessages = (payload: RejectedCall): string[] =>
    Object.entries(payload)
        .filter(([key]) => key !== 'status' && key !== 'statusText' && key !== 'error')
        .flatMap(([, fieldErrors]) =>
            typeof fieldErrors === 'object' && fieldErrors !== null
                ? Object.values(fieldErrors)
                : [],
        )
        .filter((message): message is string => typeof message === 'string' && message.length > 0)

/**
 * A message to show the user for a rejected `call`, which hands back the response body, a bare
 * `statusText`, or an `Error`, depending on how far the request got.
 *
 * A field-validation body carries no message of its own, only the marker and its per-field maps, so
 * those messages are summarised instead — the marker itself is never shown.
 */
export const getRejectionMessage = (rejection: unknown, fallback: string): string => {
    const payload = (rejection ?? {}) as RejectedCall
    const { error, statusText } = payload

    if (isFieldsRejection(payload)) {
        const messages = getFieldMessages(payload)

        return messages.length > 0 ? messages.join(' ') : fallback
    }

    if (typeof error === 'string' && error.length > 0) {
        return error
    }

    // An HTTP failure never reaches a response body, so its status line is all there is to report.
    if (typeof statusText === 'string' && statusText.length > 0) {
        return statusText
    }

    if (typeof rejection === 'string' && rejection.length > 0) {
        return rejection
    }

    if (rejection instanceof Error && rejection.message.length > 0) {
        return rejection.message
    }

    return fallback
}
